import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import AdminBackButton from "./AdminBackButton";
import { authGet, getUser, unwrapRows } from "../../lib/authApi";

const GLOBAL_ROLES = new Set(["owner", "global_admin", "global_reader", "superadmin"]);

function currentRole() {
  return String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
}

function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function statusChip(status) {
  const s = String(status || "").toLowerCase();
  const color = s === "success" ? "success" : s === "failure" ? "error" : "default";
  return <Chip size="small" label={status || "—"} color={color} variant="outlined" />;
}

function metadataText(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export default function AuditLogs() {
  const role = currentRole();
  const isGlobal = GLOBAL_ROLES.has(role);
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({
    org_id: "",
    action: "",
    actor: "",
    since: toDateInputValue(new Date(Date.now() - 24 * 60 * 60 * 1000)),
    limit: 100,
  });

  const queryString = () => {
    const params = new URLSearchParams();
    if (filters.org_id) params.set("org_id", filters.org_id);
    if (filters.action) params.set("action", filters.action.trim());
    if (filters.actor) params.set("actor", filters.actor.trim());
    if (filters.since) params.set("since", new Date(filters.since).toISOString());
    params.set("limit", String(filters.limit || 100));
    return params.toString();
  };

  const load = async () => {
    if (!isGlobal) return;
    setBusy(true);
    setErr("");
    try {
      const data = await authGet(`/api/admin/audit-logs?${queryString()}`);
      setRows(unwrapRows(data));
    } catch (e) {
      setErr(e.message || "Failed to load audit logs.");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isGlobal) return;
    let cancelled = false;
    async function init() {
      try {
        const orgResp = await authGet("/api/admin/organizations");
        if (!cancelled) setOrgs(unwrapRows(orgResp));
      } catch {
        if (!cancelled) setOrgs([]);
      }
      if (!cancelled) await load();
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobal]);

  const columns = useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        width: 210,
        valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : "—"),
      },
      { field: "actor_email", headerName: "Actor", minWidth: 230, flex: 1 },
      { field: "actor_role", headerName: "Role", width: 130 },
      { field: "action", headerName: "Action", minWidth: 210, flex: 1 },
      { field: "resource_type", headerName: "Resource", width: 150 },
      { field: "resource_id", headerName: "Resource ID", minWidth: 170, flex: 1 },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => statusChip(params.value),
      },
      { field: "ip_address", headerName: "IP", width: 150 },
      {
        field: "metadata",
        headerName: "Metadata",
        minWidth: 280,
        flex: 1,
        valueGetter: (params) => metadataText(params.row?.metadata),
      },
    ],
    []
  );

  if (!isGlobal) {
    return (
      <Box p={3} sx={{ pb: 6 }}>
        <AdminBackButton />
        <Header title="Audit Logs" subtitle="Global admin / global reader access required" />
        <Alert severity="warning">
          Audit logs can contain security-sensitive organization-wide activity. Sign in as a global_admin, global_reader, or superadmin to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ pb: 6, minWidth: 0 }}>
      <AdminBackButton />
      <Header title="Audit Logs" subtitle="Review authentication, admin, patient/result, report, and specimen activity" />

      {err ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Filters
          </Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Organization"
                size="small"
                fullWidth
                value={filters.org_id}
                onChange={(e) => setFilters((s) => ({ ...s, org_id: e.target.value }))}
              >
                <MenuItem value="">All organizations</MenuItem>
                {orgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name || org.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Action contains"
                size="small"
                fullWidth
                placeholder="AUTH_LOGIN"
                value={filters.action}
                onChange={(e) => setFilters((s) => ({ ...s, action: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Actor email contains"
                size="small"
                fullWidth
                value={filters.actor}
                onChange={(e) => setFilters((s) => ({ ...s, actor: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Since"
                type="datetime-local"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.since}
                onChange={(e) => setFilters((s) => ({ ...s, since: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                select
                label="Limit"
                size="small"
                fullWidth
                value={filters.limit}
                onChange={(e) => setFilters((s) => ({ ...s, limit: Number(e.target.value) }))}
              >
                {[50, 100, 250, 500].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button variant="contained" onClick={load} disabled={busy} fullWidth>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ minWidth: 0 }}>
          <Box sx={{ height: 640, width: "100%", minWidth: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={busy}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
